import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import * as React from 'react'

import { cn } from '@/lib/utils'

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border border-transparent px-2 font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        primary:
          'bg-primary text-white [a&]:hover:bg-primary/90 focus-visible:ring-primary/20',
        'primary-light': 'bg-blue-50 text-blue-700 border border-blue-200',
        success:
          'bg-success text-white [a&]:hover:bg-success/90 focus-visible:ring-success/20',
        'success-light':
          'bg-emerald-50 text-emerald-700 border border-emerald-200',
        destructive:
          'bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20',
        'destructive-light': 'bg-red-50 text-red-700 border border-red-200',
        warning:
          'bg-warning text-white [a&]:hover:bg-warning/90 focus-visible:ring-warning/20',
        'warning-light':
          'bg-amber-100 text-white border border-amber-300 text-amber-600 focus-visible:ring-warning/20',
        black: 'bg-black text-white focus-visible:ring-destructive/20',
        outline:
          'border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        ghost: '[a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 [a&]:hover:underline',
      },
      size: {
        lg: 'py-0.5',
        default: 'text-[12px] py-0',
        sm: 'text-[10px] py-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Badge({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span'

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export { Badge, badgeVariants }
